'use client';

import { FormEvent, useState } from 'react';
import StoreIcon from './StoreIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('Only Picel asset updates. No spam.');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state === 'pending') return;
    const form = new FormData(event.currentTarget);
    setState('pending');
    setMessage('Subscribing…');
    try {
      const response = await fetch('/api/production-commerce?route=newsletter-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, source: 'golden-storefront', website: String(form.get('website') || '') }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
      setState('success');
      const successMsg = 'You’re subscribed. New Picel assets will land here first.';
      setMessage(successMsg);
      toast.success(successMsg);
      setEmail('');
    } catch (error: any) {
      setState('error');
      const errorMsg = error?.message === 'invalid_email' ? 'Enter a valid email address.' : 'Subscription is unavailable right now. Please try again later.';
      setMessage(errorMsg);
      toast.error(errorMsg);
    }
  }

  return (
    <section
      className="relative overflow-hidden my-8 p-6 md:p-10 border border-[#5b5b46] rounded-md flex flex-col md:flex-row items-center gap-8 justify-between bg-cover bg-center"
      style={{ backgroundImage: "linear-gradient(90deg,#07152240,#071522dd 30%,#071522ee 78%,#07152288),url('/storefront/moonlit-lake.webp')" }}
      aria-labelledby="newsletter-title"
    >
      <div className="flex-1 min-w-0">
        <span className="text-xs font-bold tracking-widest text-primary block mb-1">
          A LITTLE MAGIC IN YOUR INBOX
        </span>
        <h2 id="newsletter-title" className="text-2xl md:text-3xl font-bold font-serif text-foreground">
          Get New <em className="text-amber-300 not-italic">Free Assets</em> First
        </h2>
        <p className="text-muted-foreground text-sm mt-2 mb-4">
          Fresh sprites, production-ready packs and little sparks of inspiration for your next world.
        </p>
        <form className="flex items-center gap-2 p-1.5 border border-border/80 rounded-md bg-card/80 backdrop-blur max-w-lg" aria-describedby="newsletter-status" onSubmit={submit}>
          <div className="pl-2.5 text-muted-foreground">
            <StoreIcon name="mail" />
          </div>
          <Input
            aria-label="Email address"
            autoComplete="email"
            placeholder="Your email address"
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            required
            disabled={state === 'pending' || state === 'success'}
            className="border-0 bg-transparent h-9 text-sm focus-visible:ring-0 focus-visible:outline-none"
          />
          <input className="hidden" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <Button
            type="submit"
            variant="default"
            size="sm"
            loading={state === 'pending'}
            disabled={state === 'success'}
            className="h-9 px-4 font-semibold shrink-0 rounded-md"
          >
            {state === 'pending' ? 'Subscribing…' : state === 'success' ? 'Subscribed' : 'Subscribe'}
          </Button>
        </form>
        <p id="newsletter-status" className={`text-xs mt-2 ${state === 'error' ? 'text-destructive' : state === 'success' ? 'text-emerald-400' : 'text-muted-foreground'}`} role="status" aria-live="polite">
          {message}
        </p>
      </div>
      <ul className="hidden md:flex flex-col gap-3 text-xs text-muted-foreground shrink-0">
        <li className="flex items-center gap-2">
          <StoreIcon name="check"/>
          <span>New asset releases</span>
        </li>
        <li className="flex items-center gap-2">
          <StoreIcon name="check"/>
          <span>Creator tips</span>
        </li>
        <li className="flex items-center gap-2">
          <StoreIcon name="check"/>
          <span>Special updates</span>
        </li>
      </ul>
    </section>
  );
}
